import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ViewToggle, type ViewMode } from "./view-toggle.tsx";
import { UrenStatusTabs, type StatusTab } from "./uren-status-tabs.tsx";
import { UrenFilterChips, type UrenFilterChipState } from "./uren-filter-chips.tsx";

interface UrenPageHeaderProps {
  isoYear: number;
  isoWeek: number;
  dates: string[];
  view: ViewMode;
  onViewChange: (v: ViewMode) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onTodayWeek: () => void;
  tab: StatusTab;
  counts: { todo: number; ok: number; rejected: number; exported: number };
  onTabChange: (t: StatusTab) => void;
  filterValue: UrenFilterChipState;
  onFilterChange: (v: UrenFilterChipState) => void;
  related: {
    users: { id: string; name: string }[];
    workorders: { id: string; number: string }[];
    cranes: { id: string; number: string }[];
  };
  onPrefetchTab?: (t: StatusTab) => void;
  onPrefetchView?: (v: ViewMode) => void;
  onPrefetchPrevWeek?: () => void;
  onPrefetchNextWeek?: () => void;
}

const NL_MONTH_SHORT = [
  "JAN",
  "FEB",
  "MRT",
  "APR",
  "MEI",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OKT",
  "NOV",
  "DEC",
];

function fmtRange(dates: string[]): string {
  if (dates.length !== 7) return "";
  const monStr = dates[0]!.slice(8, 10);
  const sunStr = dates[6]!.slice(8, 10);
  const monMonth = parseInt(dates[0]!.slice(5, 7), 10) - 1;
  const sunMonth = parseInt(dates[6]!.slice(5, 7), 10) - 1;
  if (monMonth === sunMonth) {
    return `${parseInt(monStr, 10)} – ${parseInt(sunStr, 10)} ${NL_MONTH_SHORT[monMonth]} ${dates[6]!.slice(0, 4)}`;
  }
  return `${parseInt(monStr, 10)} ${NL_MONTH_SHORT[monMonth]} – ${parseInt(sunStr, 10)} ${NL_MONTH_SHORT[sunMonth]} ${dates[6]!.slice(0, 4)}`;
}

export function UrenPageHeader(props: UrenPageHeaderProps) {
  const { t } = useTranslation();
  const range = fmtRange(props.dates);
  return (
    <div className="bg-surface md:border-b md:border-line">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-4 md:px-6 md:py-3.5">
        <div className="flex items-baseline gap-3">
          <h1 className="font-sans-condensed text-xl font-semibold text-ink-900 md:text-[18px]">
            {t("admin.uren.title")}
          </h1>
          <span className="hidden font-mono text-[11px] uppercase tracking-wider text-ink-500 md:inline">
            ·{" "}
            <strong className="text-ink-900">
              {t("admin.uren.kicker", { week: props.isoWeek, range })}
            </strong>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <ViewToggle
            value={props.view}
            onChange={props.onViewChange}
            onPrefetch={props.onPrefetchView}
          />
          <button
            onClick={props.onTodayWeek}
            className="text-xs text-ink-700 px-2"
          >{`Vandaag`}</button>
          <div className="inline-flex gap-1">
            <button
              aria-label="Vorige week"
              onClick={props.onPrevWeek}
              onMouseEnter={props.onPrefetchPrevWeek}
              onFocus={props.onPrefetchPrevWeek}
              className="size-6 rounded border border-line bg-surface"
            >
              <ChevronLeft size={14} className="mx-auto" />
            </button>
            <button
              aria-label="Volgende week"
              onClick={props.onNextWeek}
              onMouseEnter={props.onPrefetchNextWeek}
              onFocus={props.onPrefetchNextWeek}
              className="size-6 rounded border border-line bg-surface"
            >
              <ChevronRight size={14} className="mx-auto" />
            </button>
          </div>
        </div>
      </div>
      <div className="px-4 md:px-6">
        <UrenStatusTabs
          value={props.tab}
          counts={props.counts}
          onChange={props.onTabChange}
          onPrefetch={props.onPrefetchTab}
        />
      </div>
      <div className="flex items-center justify-between px-4 py-2.5 md:px-6">
        <UrenFilterChips
          value={props.filterValue}
          onChange={props.onFilterChange}
          related={props.related}
        />
        <div />
      </div>
    </div>
  );
}
